<!-- eslint-disable -->
<template>
  <v-dialog v-model="dialog" max-width="500" scrollable>
    <template v-slot:activator="{ props }">
      <v-btn icon v-bind="props" size="small" :title="$t('accessibility.title')">
        <v-icon>mdi-cog</v-icon>
      </v-btn>
    </template>
    
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-cog</v-icon>
        {{ $t('accessibility.title') }}
      </v-card-title>
      
      <v-card-text>
        <!-- Theme Selection -->
        <div class="mb-6">
          <div class="d-flex align-center mb-2">
            <v-icon class="mr-2">mdi-palette</v-icon>
            <span class="text-subtitle-2">{{ $t('accessibility.colorTheme') }}</span>
          </div>
          <v-select
            v-model="settings.theme"
            :items="themeOptionsList"
            item-title="name"
            item-value="key"
            variant="outlined"
            density="compact"
          >
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-icon :color="getThemePreviewColor(item.raw.key)">{{ item.raw.icon }}</v-icon>
                </template>
              </v-list-item>
            </template>
            <template v-slot:selection="{ item }">
              <v-icon :color="getThemePreviewColor(item.raw.key)" class="mr-2">{{ item.raw.icon }}</v-icon>
              {{ item.raw.name }}
            </template>
          </v-select>
        </div>
        
        <!-- Font Size -->
        <div class="mb-6">
          <div class="text-subtitle-2 mb-2">{{ $t('accessibility.fontSize') }}</div>
          <v-btn-toggle v-model="settings.fontSize" mandatory color="primary" class="flex-wrap">
            <v-btn value="small" size="small">
              <span style="font-size: 10px;">A</span>
            </v-btn>
            <v-btn value="medium" size="small">
              <span style="font-size: 12px;">A</span>
            </v-btn>
            <v-btn value="large" size="small">
              <span style="font-size: 14px;">A</span>
            </v-btn>
            <v-btn value="xlarge" size="small">
              <span style="font-size: 16px;">A</span>
            </v-btn>
            <v-btn value="xxlarge" size="small">
              <span style="font-size: 20px;">A</span>
            </v-btn>
            <v-btn value="xxxlarge" size="small">
              <span style="font-size: 24px;">A</span>
            </v-btn>
          </v-btn-toggle>
          <div class="text-caption text-grey mt-1">
            {{ $t('accessibility.fontSizeHint') }}
          </div>
        </div>
        
        <!-- Text-to-Speech -->
        <div class="mb-4">
          <v-switch
            v-model="settings.textToSpeechEnabled"
            :label="$t('accessibility.textToSpeech')"
            color="primary"
            hide-details
          >
            <template v-slot:prepend>
              <v-icon>mdi-text-to-speech</v-icon>
            </template>
          </v-switch>
          <div class="text-caption text-grey ml-10">
            {{ $t('accessibility.textToSpeechHint') }}
          </div>
        </div>
        
        <!-- Test TTS Button -->
        <v-btn
          v-if="settings.textToSpeechEnabled"
          variant="outlined"
          size="small"
          @click="testSpeech"
          class="ml-10 mb-4"
        >
          <v-icon start>mdi-play</v-icon>
          {{ $t('accessibility.testSpeech') }}
        </v-btn>

        <!-- Reader Mode -->
        <div class="mb-4">
          <v-switch
            v-model="settings.readerModeEnabled"
            :label="$t('accessibility.readerMode')"
            color="primary"
            hide-details
          >
            <template v-slot:prepend>
              <v-icon>mdi-book-open-page-variant</v-icon>
            </template>
          </v-switch>
          <div class="text-caption text-grey ml-10">
            {{ $t('accessibility.readerModeHint') }}
          </div>
        </div>
        
        <!-- Translate To -->
        <div class="mb-4">
          <div class="d-flex align-center mb-2">
            <v-icon class="mr-2">mdi-translate</v-icon>
            <span class="text-subtitle-2">{{ $t('accessibility.translateTo') }}</span>
          </div>
          <v-select
            v-model="settings.translateTo"
            :items="translateLanguages"
            item-title="name"
            item-value="code"
            variant="outlined"
            density="compact"
            clearable
            :placeholder="$t('accessibility.noTranslate')"
          ></v-select>
          <div class="text-caption text-grey">
            {{ $t('accessibility.translateHint') }}
          </div>
        </div>

        <!-- Favorite Group -->
        <div class="mb-4">
          <div class="d-flex align-center mb-2">
            <v-icon class="mr-2">mdi-star</v-icon>
            <span class="text-subtitle-2">{{ $t('accessibility.favoriteGroup') }}</span>
          </div>
          <v-select
            v-model="settings.favoriteGroupId"
            :items="userGroups"
            item-title="groupName"
            item-value="groupId"
            variant="outlined"
            density="compact"
            clearable
            :placeholder="$t('accessibility.noFavoriteGroup')"
            :loading="loadingGroups"
          >
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-avatar size="28" rounded="lg">
                    <v-img 
                      :src="item.raw.coverImageUrl || `https://picsum.photos/seed/${item.raw.groupId}/100/100`"
                      cover
                    ></v-img>
                  </v-avatar>
                </template>
              </v-list-item>
            </template>
            <template v-slot:selection="{ item }">
              <v-avatar size="24" rounded="lg" class="mr-2">
                <v-img 
                  :src="item.raw.coverImageUrl || `https://picsum.photos/seed/${item.raw.groupId}/100/100`"
                  cover
                ></v-img>
              </v-avatar>
              {{ item.raw.groupName }}
            </template>
          </v-select>
          <div class="text-caption text-grey">
            {{ $t('accessibility.favoriteGroupHint') }}
          </div>
        </div>
      </v-card-text>
      
      <v-card-actions>
        <v-btn variant="text" @click="resetSettings">
          {{ $t('accessibility.reset') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="dialog = false">
          {{ $t('common.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
/* eslint-disable */
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import { accessibilitySettings, speak } from '../services/accessibility'
import { themeOptions } from '../plugins/vuetify'
import api from '../services/api'

const { t } = useI18n()
const vuetifyTheme = useTheme()
const dialog = ref(false)

const settings = accessibilitySettings
const themeOptionsList = themeOptions

const userGroups = ref([])
const loadingGroups = ref(false)

const translateLanguages = [
  { code: 'da', name: 'Dansk' },
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'sv', name: 'Svenska' },
  { code: 'nb', name: 'Norsk' },
  { code: 'th', name: 'ไทย' },
  { code: 'pl', name: 'Polski' },
  { code: 'fr', name: 'Français' },
  { code: 'pt', name: 'Português' },
  { code: 'tl', name: 'Tagalog' }
]

const themePreviewColors = {
  default: '#4A8B7F',
  twitter: '#1DA1F2',
  facebook: '#1877F2',
  purple: '#7C4DFF',
  warm: '#FF7043',
  dark: '#4A8B7F',
  twitterDark: '#1DA1F2',
  highContrast: '#000000'
}

const getThemePreviewColor = (key) => themePreviewColors[key] || '#4A8B7F'

const testSpeech = () => {
  speak(t('accessibility.testMessage'))
}

const loadUserGroups = async () => {
  loadingGroups.value = true
  try {
    const response = await api.get('/groups', { params: { my: 'true' } })
    userGroups.value = response.data.groups || []
  } catch (error) {
    console.error('Error loading user groups:', error)
  } finally {
    loadingGroups.value = false
  }
}

const resetSettings = () => {
  settings.fontSize = 'medium'
  settings.highContrast = false
  settings.textToSpeechEnabled = false
  settings.readerModeEnabled = false
  settings.translateTo = ''
  settings.theme = 'default'
  settings.favoriteGroupId = null
}

// Load groups when dialog opens
watch(dialog, (isOpen) => {
  if (isOpen) {
    loadUserGroups()
  }
})

// Watch for theme changes and apply
watch(() => settings.theme, (newTheme) => {
  vuetifyTheme.global.name.value = newTheme
}, { immediate: true })
</script>